import { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  QRCode,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DownloadOutlined,
  LinkOutlined,
  PlusOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { createBusinessQrRecord, fetchBusinessQrsList } from "../../api/businessQrs";
import { formatCareDateTime } from "../../hooks/columns/tableColumnUtils";
import { useCareList } from "../../hooks/useCareList";
import {
  formatBusinessDisplayName,
  QR_BRAND_ICON,
  slugifyBusinessName,
} from "../../utils/businessQr";
import { useSelector } from "react-redux";
import "./style.less";

function StatTile({ icon, label, value, loading, accent = "teal" }) {
  return (
    <div className={`care-qr-stat care-qr-stat--${accent}`}>
      <div className="care-qr-stat-icon" aria-hidden>
        {icon}
      </div>
      <div className="care-qr-stat-body">
        <span className="care-qr-stat-label">{label}</span>
        <Spin spinning={loading} size="small">
          <span className="care-qr-stat-value">{value}</span>
        </Spin>
      </div>
    </div>
  );
}

async function copyTextToClipboard(text, successMessage = "Link copied") {
  try {
    await navigator.clipboard.writeText(text);
    message.success(successMessage);
  } catch {
    message.error("Could not copy link");
  }
}

function canvasToWhiteBackgroundPng(sourceCanvas) {
  const padding = Math.max(12, Math.round(sourceCanvas.width * 0.1));
  const exportCanvas = document.createElement("canvas");
  exportCanvas.width = sourceCanvas.width + padding * 2;
  exportCanvas.height = sourceCanvas.height + padding * 2;

  const ctx = exportCanvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
  ctx.drawImage(sourceCanvas, padding, padding);

  return exportCanvas.toDataURL("image/png");
}

function BusinessQrCode({ value, size, className }) {
  const iconSize = Math.round(size * 0.28);

  return (
    <QRCode
      className={className}
      errorLevel="H"
      value={value}
      size={size}
      icon={QR_BRAND_ICON}
      iconSize={iconSize}
      bordered={false}
    />
  );
}

function QrBusinessCard({ entry, onOpen }) {
  const displayName = formatBusinessDisplayName(entry.businessName);

  return (
    <article
      className="care-qr-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(entry);
        }
      }}
      aria-label={`View details for ${displayName}`}
    >
      <div className="care-qr-card-qr-frame">
        <BusinessQrCode value={entry.url} size={108} className="care-qr-code" />
      </div>
      <div className="care-qr-card-body">
        <Typography.Text className="care-qr-card-title" ellipsis={{ tooltip: displayName }}>
          {displayName}
        </Typography.Text>
        <div className="care-qr-card-meta">
          <Tag color="green" icon={<DownloadOutlined />} className="care-qr-card-downloads">
            {entry.downloadCount ?? 0} downloads
          </Tag>
          <span className="care-qr-card-hint">View details</span>
        </div>
      </div>
    </article>
  );
}

function QrDetailModal({ entry, open, onClose }) {
  const qrWrapRef = useRef(null);

  const downloadQrPng = useCallback(() => {
    if (!entry) return;

    const canvas = qrWrapRef.current?.querySelector("canvas");
    if (!canvas) {
      message.error("Could not download QR code");
      return;
    }

    const pngDataUrl = canvasToWhiteBackgroundPng(canvas);
    if (!pngDataUrl) {
      message.error("Could not download QR code");
      return;
    }

    const slug = slugifyBusinessName(entry.businessName) || "business-qr";
    const link = document.createElement("a");
    link.href = pngDataUrl;
    link.download = `${slug}-qr.png`;
    link.click();
    message.success("QR code downloaded");
  }, [entry]);

  if (!entry) return null;

  const copyUrl = () => copyTextToClipboard(entry.url);

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button key="copy" type="primary" icon={<LinkOutlined />} onClick={copyUrl}>
          Copy link
        </Button>,
      ]}
      width={520}
      className="care-qr-detail-modal"
      centered
      destroyOnClose
    >
      <div className="care-qr-detail-head">
        <span className="care-qr-detail-icon" aria-hidden>
          <ShopOutlined />
        </span>
        <div className="care-qr-detail-head-text">
          <Typography.Title level={4} className="care-qr-detail-title">
            {formatBusinessDisplayName(entry.businessName)}
          </Typography.Title>
          <Typography.Text type="secondary">
            Created {formatCareDateTime(entry.createdAt)}
          </Typography.Text>
        </div>
      </div>

      <div
        ref={qrWrapRef}
        className="care-qr-detail-qr-wrap care-qr-detail-qr-wrap--downloadable"
        role="button"
        tabIndex={0}
        title="Click to download PNG"
        aria-label={`Download QR code for ${entry.businessName}`}
        onClick={downloadQrPng}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            downloadQrPng();
          }
        }}
      >
        <div className="care-qr-detail-qr-wrap-inner">
          <BusinessQrCode
            value={entry.url}
            size={300}
            className="care-qr-code care-qr-code--detail"
          />
        </div>
        <span className="care-qr-detail-qr-hint">
          <DownloadOutlined aria-hidden /> Click to download PNG
        </span>
      </div>

      <div className="care-qr-detail-row">
        <span className="care-qr-detail-label">Downloads</span>
        <Tag
          color="green"
          variant="outlined"
          icon={<DownloadOutlined />}
          className="care-qr-detail-downloads"
        >
          {entry.downloadCount ?? 0}
        </Tag>
      </div>

      <div className="care-qr-detail-row care-qr-detail-row--url">
        <div
          className="care-qr-detail-url-box"
          role="button"
          tabIndex={0}
          title="Click to copy link"
          onClick={copyUrl}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              copyUrl();
            }
          }}
        >
          <LinkOutlined className="care-qr-detail-url-icon" aria-hidden />
          <Typography.Text className="care-qr-detail-url">{entry.url}</Typography.Text>
        </div>
      </div>
    </Modal>
  );
}

export default function QrManage() {
  const token = useSelector((state) => state.auth.token);
  const [form] = Form.useForm();
  const [historySearch, setHistorySearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);

  const { data: entries, loading, error, reload } = useCareList(fetchBusinessQrsList, token);

  const totalDownloads = useMemo(
    () => entries.reduce((sum, e) => sum + (e.downloadCount || 0), 0),
    [entries],
  );

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((entry) => {
      const haystack = [
        entry.businessName,
        entry.businessSlug,
        String(entry.downloadCount ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, historySearch]);

  const openDetail = useCallback((entry) => {
    setDetailEntry(entry);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailEntry(null);
  }, []);

  const openAddModal = useCallback(() => {
    form.resetFields();
    setAddModalOpen(true);
  }, [form]);

  const closeAddModal = useCallback(() => {
    setAddModalOpen(false);
    form.resetFields();
  }, [form]);

  const handleGenerate = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const label = values.businessName.trim();
    const nameKey = label.toLowerCase();
    const duplicate = entries.some((e) => e.businessName.trim().toLowerCase() === nameKey);
    if (duplicate) {
      message.info("A QR for this business already exists.");
      closeAddModal();
      return;
    }

    setSubmitting(true);
    try {
      await createBusinessQrRecord(token, { name: label });
      closeAddModal();
      message.success(`QR created for ${label}`);
      reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Could not create QR");
    } finally {
      setSubmitting(false);
    }
  }, [entries, form, token, closeAddModal, reload]);

  return (
    <div className="care-page care-qr-manage">
      <section className="care-qr-top" aria-label="QR overview">
        <div className="care-qr-top-main">
          <Typography.Title level={4} className="care-qr-hero-title">
            Partner download QR codes
          </Typography.Title>
          <Typography.Paragraph className="care-qr-hero-lead">
            Generate tracked links for gyms, clinics, pharmacies, and other partners. Each scan
            attributes installs to the business name on your banner.
          </Typography.Paragraph>
          <Button
            color="blue"
            variant="outlined"
            className="care-qr-refresh-btn"
            icon={<ReloadOutlined />}
            onClick={reload}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
        <div className="care-qr-top-stats">
          <StatTile
            accent="teal"
            icon={<QrcodeOutlined />}
            label="Businesses with QR"
            value={entries.length.toLocaleString("en-IN")}
            loading={loading}
          />
          <StatTile
            accent="cyan"
            icon={<DownloadOutlined />}
            label="Total APK downloads"
            value={totalDownloads.toLocaleString("en-IN")}
            loading={loading}
          />
        </div>
      </section>

      <section className="care-qr-gallery-shell">
        <header className="care-qr-gallery-toolbar">
          <div className="care-qr-gallery-toolbar-start">
            <Typography.Title level={5} className="care-qr-gallery-heading">
              QR library
            </Typography.Title>
            <Typography.Text type="secondary" className="care-qr-gallery-sub">
              {loading ? "Loading…" : `${filteredHistory.length} of ${entries.length} shown`}
            </Typography.Text>
          </div>
          <div className="care-qr-gallery-toolbar-actions">
            <Input.Search
              allowClear
              className="care-qr-gallery-search"
              placeholder="Search by business name or URL…"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
            />
            <Button
              type="primary"
              className="care-qr-gallery-add-btn"
              icon={<PlusOutlined />}
              onClick={openAddModal}
            >
              Add business QR
            </Button>
          </div>
        </header>

        <div className="care-qr-gallery-body">
          {error ? (
            <Alert
              type="error"
              showIcon
              message={error.message || "Failed to load QR codes"}
              className="care-qr-gallery-alert"
            />
          ) : null}

          <Spin spinning={loading && entries.length > 0} tip="Updating…">
            {!error && !loading && entries.length === 0 ? (
              <div className="care-qr-empty">
                <div className="care-qr-empty-icon" aria-hidden>
                  <QrcodeOutlined />
                </div>
                <Typography.Title level={4} className="care-qr-empty-title">
                  No QR codes yet
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="care-qr-empty-text">
                  Create your first partner QR so installs from banners can be tracked per business.
                </Typography.Paragraph>
                <Button type="primary" size="large" icon={<PlusOutlined />} onClick={openAddModal}>
                  Add business QR
                </Button>
              </div>
            ) : null}

            {!error && !loading && entries.length > 0 && filteredHistory.length === 0 ? (
              <Empty
                className="care-qr-gallery-empty"
                description="No QR codes match your search."
              />
            ) : null}

            {!error && filteredHistory.length > 0 ? (
              <div className="care-qr-grid">
                {filteredHistory.map((entry) => (
                  <QrBusinessCard key={entry.id} entry={entry} onOpen={openDetail} />
                ))}
              </div>
            ) : null}
          </Spin>
        </div>
      </section>

      <QrDetailModal entry={detailEntry} open={detailEntry != null} onClose={closeDetail} />

      <Modal
        title={null}
        open={addModalOpen}
        onCancel={closeAddModal}
        onOk={handleGenerate}
        okText="Generate QR"
        cancelText="Cancel"
        confirmLoading={submitting}
        destroyOnClose
        width={480}
        className="care-qr-modal"
        centered
      >
        <div className="care-qr-modal-head">
          <span className="care-qr-modal-icon" aria-hidden>
            <QrcodeOutlined />
          </span>
          <div>
            <Typography.Title level={4} className="care-qr-modal-title">
              New business QR
            </Typography.Title>
            <Typography.Text type="secondary">
              The name appears on the download link and in your reports.
            </Typography.Text>
          </div>
        </div>

        <Form form={form} layout="vertical" requiredMark="optional" className="care-qr-modal-form">
          <Form.Item
            label="Business name"
            name="businessName"
            rules={[
              { required: true, message: "Enter the business name" },
              { whitespace: true, message: "Enter the business name" },
              { min: 2, message: "At least 2 characters" },
            ]}
          >
            <Input
              size="large"
              placeholder="e.g. Gold Gym Downtown, City Pharmacy"
              allowClear
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
